import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { UserContext } from '../context/UserContext';
import StockService from '../Api_Module/Api_Services/StockService';
import { formatNumber } from '../utils/Numberformat';
import { TrendingUp, TrendingDown, Lock, ArrowLeft } from 'lucide-react';
import LockPill from '../components/common/LockPill';

interface InstitutionHolding {
    date: string;
    filingDate: string;
    cik: string;
    securityCusip: string;
    symbol: string | null;
    nameOfIssuer: string;
    shares: number;
    titleOfClass: string;
    sharesType: string;
    putCallShare: string;
    value: number;
    portfolioWeight: number;
    link: string;
    finalLink: string;
    previousShares: number | null;
    previousValue: number | null;
    changeInShares: number;
    changeInSharesPercentage: number | null;
    changeInValue: number; // Capital Flow
    changeInValuePercentage: number | null;
    changeInStake: number; // Change in portfolio weight
    isNew: boolean;
    isClosed: boolean;
}

const InstitutionPortfolio: React.FC = () => {
    const { cik } = useParams<{ cik: string }>();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const userContext = React.useContext(UserContext);
    const userData = userContext?.userData;
    const hasValidAccess = Boolean(
        userData && (
            (userData.subscriptionStatus === 'active' && userData.planActive === true) ||
            (userData.subscriptionStatus === 'trialing' && userData.planActive === true && (userData as any).isTrial === true) ||
            ((userData as any).isTrial === true && ((userData as any).daysLeft ?? 0) > 0)
        )
    );

    // Revert: remove period state and navigator; we'll compute period on fetch
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [portfolioData, setPortfolioData] = useState<{ holdings: InstitutionHolding[] } | null>(null);
    const [portfolioMeta, setPortfolioMeta] = useState<{ page: number; size: number; total: number; totalPages: number; hasMore: boolean; totalPortfolioValue?: number; previousTotalPortfolioValue?: number; totalCapitalFlow?: number; period?: string; performanceSummary?: any } | null>(null);
    const [topBuys, setTopBuys] = useState<InstitutionHolding[]>([]);
    const [topSells, setTopSells] = useState<InstitutionHolding[]>([]);
    const [institutionName, setInstitutionName] = useState<string>('');
    const [institutionLogoUrl, setInstitutionLogoUrl] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    // Filter states for holdings table
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searchInput, setSearchInput] = useState<string>(''); // For immediate UI feedback
    const [assetTypeFilter, setAssetTypeFilter] = useState<string>('stock'); // default to 'stock' (Equities)
    const [isFilterLoading, setIsFilterLoading] = useState<boolean>(false);

    // Column visibility state for restrictions
    const [columnVisibility, setColumnVisibility] = useState({
        marketValue: true,
        portfolioWeight: true,
        shares: true,
        changeInStake: true,
        capitalFlow: true,
    });
    const [showRestrictions, setShowRestrictions] = useState<boolean>(false);

    // Theme-aware colors
    const bgBase = theme === 'dark' ? 'bg-gray-900' : 'bg-white';
    const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
    const textSecondary = theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
    const cardBorder = theme === 'dark' ? 'border-gray-700' : 'border-gray-200';
    const cardBg = theme === 'dark' ? 'bg-gray-800' : 'bg-white';

    useEffect(() => {
        if (!cik) return;

        const fetchData = async () => {
            try {
                setLoading(true);
                // Compute most recent completed quarter (previous quarter)
                const now = new Date();
                const y = now.getFullYear();
                const q = Math.floor(now.getMonth() / 3) + 1;
                const prevQ = q - 1 <= 0 ? 4 : q - 1;
                const prevY = q - 1 <= 0 ? y - 1 : y;
                const period = `${prevY} Q${prevQ}`;

                const [portfolio, summary, buys, sells] = await Promise.all([
                    StockService.getInstitutionPortfolioWithChanges(cik, { period, page: 0, size: 25 }),
                    StockService.getInstitutionPortfolioSummary(cik, { period }),
                    StockService.getInstitutionTopBuys(cik, { period, limit: 5 }),
                    StockService.getInstitutionTopSells(cik, { period, limit: 5 }),
                ]);

                const holdingsArray = portfolio?.holdings || portfolio?.content || portfolio || [];
                setPortfolioData({ holdings: Array.isArray(holdingsArray) ? holdingsArray : [] });
                setPortfolioMeta({
                    page: portfolio?.page ?? 0,
                    size: portfolio?.size ?? (Array.isArray(holdingsArray) ? holdingsArray.length : 0),
                    total: portfolio?.total ?? (Array.isArray(holdingsArray) ? holdingsArray.length : 0),
                    totalPages: portfolio?.totalPages ?? 1,
                    hasMore: Boolean(portfolio?.hasMore ?? ((portfolio?.page ?? 0) + 1 < (portfolio?.totalPages ?? 1))),
                    totalPortfolioValue: summary?.totalPortfolioValue ?? portfolio?.totalPortfolioValue,
                    previousTotalPortfolioValue: summary?.previousTotalPortfolioValue ?? portfolio?.previousTotalPortfolioValue,
                    totalCapitalFlow: summary?.totalCapitalFlow ?? portfolio?.totalCapitalFlow,
                    period: summary?.period ?? portfolio?.period ?? period,
                    performanceSummary: summary?.performanceSummary,
                    newPositions: summary?.newPositions,
                    closedPositions: summary?.closedPositions,
                    increased: summary?.increased,
                    reduced: summary?.reduced,
                } as any);
                setTopBuys(Array.isArray(buys) ? buys : []);
                setTopSells(Array.isArray(sells) ? sells : []);

                const perfInvestorName = portfolio?.performanceSummary?.investorName;
                const perfLogoUrl = portfolio?.performanceSummary?.logoUrl;

                // Debug: Log the performance summary structure
                console.log('Performance Summary from API:', portfolio?.performanceSummary);

                if (perfInvestorName) {
                    setInstitutionName(perfInvestorName);
                } else {
                    const firstHolding = Array.isArray(holdingsArray) ? holdingsArray[0] : null;
                    setInstitutionName((firstHolding && (firstHolding as any).institutionName) || `Institution ${cik}`);
                }

                if (perfLogoUrl) {
                    setInstitutionLogoUrl(perfLogoUrl);
                }
            } catch (err: any) {
                console.error('Error fetching institution portfolio:', err);
                setError(err?.message || 'Failed to load institution portfolio');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [cik]);

    const loadMore = async () => {
        if (!portfolioMeta || !cik) return;
        const nextPage = portfolioMeta.page + 1;
        if (portfolioMeta.hasMore === false) return;
        if (loadingMore) return;
        setLoadingMore(true);
        try {
            const params: any = {
                period: portfolioMeta.period,
                page: nextPage,
                size: portfolioMeta.size || 25
            };

            // Apply current filters to loadMore
            if (searchQuery.trim()) {
                params.search = searchQuery.trim();
            }

            if (assetTypeFilter !== 'all') {
                if (assetTypeFilter === 'stock') {
                    params.positionType = 'Equities';
                } else if (assetTypeFilter === 'call') {
                    params.positionType = 'CALLS';
                } else if (assetTypeFilter === 'put') {
                    params.positionType = 'PUTS';
                }
            }

            const resp = await StockService.getInstitutionPortfolioWithChanges(cik, params);
            const nextHoldings = resp?.holdings || resp?.content || [];
            setPortfolioData(prev => ({ holdings: [...(prev?.holdings || []), ...nextHoldings] }));
            setPortfolioMeta({
                page: resp?.page ?? nextPage,
                size: resp?.size ?? portfolioMeta.size,
                total: resp?.total ?? portfolioMeta.total,
                totalPages: resp?.totalPages ?? portfolioMeta.totalPages,
                hasMore: Boolean(resp?.hasMore ?? ((resp?.page ?? nextPage) + 1 < (resp?.totalPages ?? portfolioMeta.totalPages))),
                totalPortfolioValue: resp?.totalPortfolioValue ?? portfolioMeta.totalPortfolioValue,
                previousTotalPortfolioValue: resp?.previousTotalPortfolioValue ?? portfolioMeta.previousTotalPortfolioValue,
                totalCapitalFlow: resp?.totalCapitalFlow ?? portfolioMeta.totalCapitalFlow,
                period: resp?.period ?? portfolioMeta.period,
                performanceSummary: resp?.performanceSummary ?? portfolioMeta.performanceSummary,
            });
        } catch (e) {
            console.error('Failed to load more holdings:', e);
        } finally {
            setLoadingMore(false);
        }
    };

    // Infinite scroll observer
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (entry.isIntersecting && portfolioMeta?.hasMore && !loadingMore) {
                loadMore();
            }
        }, { root: null, rootMargin: '200px', threshold: 0 });
        observer.observe(el);
        return () => observer.disconnect();
    }, [portfolioMeta?.hasMore, loadingMore]);

    // Window scroll fallback: trigger loadMore when nearing bottom
    useEffect(() => {
        const onScroll = () => {
            if (!portfolioMeta?.hasMore || loadingMore) return;
            const scrollPos = window.scrollY + window.innerHeight;
            const threshold = document.body.offsetHeight - 300;
            if (scrollPos >= threshold) {
                loadMore();
            }
        };
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, [portfolioMeta?.hasMore, loadingMore]);

    // Fetch filtered data from API
    const fetchFilteredData = async (search: string = searchQuery, assetType: string = assetTypeFilter) => {
        if (!cik || !portfolioMeta?.period) return;

        setIsFilterLoading(true);
        try {
            const params: any = {
                period: portfolioMeta.period,
                page: 0,
                size: 25
            };

            if (search.trim()) {
                params.search = search.trim();
            }

            if (assetType !== 'all') {
                // Map UI filter to API positionType
                if (assetType === 'stock') {
                    params.positionType = 'Equities';
                } else if (assetType === 'call') {
                    params.positionType = 'CALLS';
                } else if (assetType === 'put') {
                    params.positionType = 'PUTS';
                }
            }

            const portfolio = await StockService.getInstitutionPortfolioWithChanges(cik, params);
            const holdingsArray = portfolio?.holdings || portfolio?.content || [];

            setPortfolioData({ holdings: Array.isArray(holdingsArray) ? holdingsArray : [] });
            setPortfolioMeta(prev => ({
                ...prev!,
                page: portfolio?.page ?? 0,
                size: portfolio?.size ?? 25,
                total: portfolio?.total ?? holdingsArray.length,
                totalPages: portfolio?.totalPages ?? 1,
                hasMore: Boolean(portfolio?.hasMore ?? ((portfolio?.page ?? 0) + 1 < (portfolio?.totalPages ?? 1))),
            }));
        } catch (err) {
            console.error('Error fetching filtered holdings:', err);
        } finally {
            setIsFilterLoading(false);
        }
    };

    // Trigger API call only when asset type filter changes (not search)
    useEffect(() => {
        if (portfolioMeta?.period) {
            fetchFilteredData(searchQuery, assetTypeFilter);
        }
    }, [assetTypeFilter]);

    // Handler for search button click
    const handleSearch = () => {
        setSearchQuery(searchInput);
        if (portfolioMeta?.period) {
            fetchFilteredData(searchInput, assetTypeFilter);
        }
    };

    // For display purposes, just use portfolioData.holdings directly
    const filteredHoldings = useMemo(() => {
        return portfolioData?.holdings || [];
    }, [portfolioData]);

    // Calculate metrics - prioritize summary data from API
    const metrics = useMemo(() => {
        if (!portfolioData?.holdings && !portfolioMeta) return null;

        const holdings = portfolioData?.holdings || [];
        const totalValue = portfolioMeta?.totalPortfolioValue ?? holdings.reduce((sum, h) => sum + (h.value || 0), 0);
        const totalChangeInValue = portfolioMeta?.totalCapitalFlow ?? holdings.reduce((sum, h) => sum + (h.changeInValue || 0), 0);

        // Use summary data from root level if available, otherwise calculate from holdings
        const newPositions = (portfolioMeta as any)?.newPositions
            ?? holdings.filter(h => h.isNew).length;
        const closedPositions = (portfolioMeta as any)?.closedPositions
            ?? holdings.filter(h => h.isClosed).length;
        const increased = (portfolioMeta as any)?.increased
            ?? holdings.filter(h => (!h.isNew && !h.isClosed && h.changeInValue > 0)).length;
        const reduced = (portfolioMeta as any)?.reduced
            ?? holdings.filter(h => (!h.isNew && !h.isClosed && h.changeInValue < 0)).length;

        const serverTotal = portfolioMeta?.performanceSummary?.portfolioSize ?? portfolioMeta?.total;

        return {
            aum: totalValue,
            aumGrowth: totalChangeInValue,
            holdingCount: serverTotal ?? holdings.length,
            newPositions,
            closedPositions,
            increased,
            reduced,
        };
    }, [portfolioData, portfolioMeta]);

    // Helper function to generate initials and color
    const generateInitials = (name: string) => {
        const parts = name.split(' ').filter(Boolean);
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    const colorFromString = (s: string) => {
        let hash = 0;
        for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
        const hue = Math.abs(hash) % 360;
        return `hsl(${hue} 80% 45%)`;
    };

    const handleStockClick = (symbol: string | null) => {
        if (symbol) {
            navigate(`/${symbol}/ownership/institutions`);
        }
    };

    // Utility: color class by sign
    const signClass = (val: number | null | undefined) => {
        if (val == null) return textSecondary;
        return val >= 0 ? '!text-green-500' : '!text-red-500';
    };

    const openAuthFromLock = (e?: React.MouseEvent) => {
        try { if (e) e.stopPropagation(); } catch {}
        try { window.dispatchEvent(new CustomEvent('sf-open-auth')); } catch {}
    };

    const LockBadge: React.FC = () => (
        <div className="mt-1">
            <LockPill size="sm" />
        </div>
    );

    if (loading) {
        return (
            <div className={`min-h-screen ${bgBase}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                    <div className={`rounded-lg border ${cardBorder} ${cardBg} p-8 flex items-center justify-center`}>
                        <span className={textSecondary}>Loading institution portfolio...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !portfolioData) {
        return (
            <div className={`min-h-screen ${bgBase}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                    <div className={`rounded-lg border ${cardBorder} ${cardBg} p-8`}>
                        <div className={`text-sm ${textSecondary}`}>{error || 'No data available'}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${bgBase}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs">
                {/* Header Section with Logo */}
                <div className={`mb-2 text-center`}>
                    <div className="flex flex-col items-center gap-1 w-full">
                        {/* Institution Logo or Initials */}
                        {institutionLogoUrl ? (
                            <img
                                src={institutionLogoUrl}
                                alt={institutionName}
                                className="w-12 h-12 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600"
                                onError={(e) => {
                                    // Fallback to initials if image fails to load
                                    e.currentTarget.style.display = 'none';
                                }}
                            />
                        ) : (
                            <div
                                className="w-12 h-12 rounded-full flex items-center justify-center text-white text-base font-bold"
                                style={{ background: colorFromString(institutionName || cik || '') }}
                            >
                                {generateInitials(institutionName || `Institution ${cik}`)}
                            </div>
                        )}
                        <h1 className={`text-sm font-semibold ${textPrimary}`}>{institutionName || `Institution ${cik}`}</h1>
                        <div className="mt-1 w-full">
                            <div className={`text-[13px] ${textSecondary} tracking-wide`}>Assets Under Management</div>
                            <div className="relative w-full flex items-center justify-center">
                                <button
                                    type="button"
                                    onClick={() => navigate('/super-investors', { state: { view: 'leaderboard' } })}
                                    className={`absolute left-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${cardBorder} ${theme === 'dark' ? 'bg-gray-800/60 hover:bg-gray-800 text-gray-200' : 'bg-white hover:bg-gray-50 text-gray-700'} shadow-sm`}
                                >
                                    <ArrowLeft className="w-3.5 h-3.5 opacity-80" />
                                    <span className="text-xs font-medium">Back to Portfolios</span>
                                </button>
                                <div className="flex items-baseline gap-2">
                                    <div className={`text-xs ${textPrimary}`}>${formatNumber((portfolioMeta?.totalPortfolioValue) ?? 0)}</div>
                                    <div className={`text-xs  ${textSecondary}`}>{portfolioMeta?.period}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Portfolio Performance - Clean & Minimal */}
                <div className="space-y-3 mb-6">
                    {/* Performance Metrics Grid */}
                    <div className={`rounded-xl border ${cardBorder} ${cardBg} overflow-hidden`}>
                        <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-y md:divide-y-0 divide-gray-700 dark:divide-gray-700">
                            {/* If performanceSummary exists, use values with color coding */}
                            {portfolioMeta?.performanceSummary ? (
                                <>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>Recent Quarter</div>
                                        {hasValidAccess ? (
                                            <>
                                                <div className={`text-sm font-semibold ${signClass(portfolioMeta.performanceSummary.performancePercentage)}`}>{(portfolioMeta.performanceSummary.performancePercentage ?? 0).toFixed(2)}%</div>
                                                <div className={`text-[11px] ${textSecondary} mt-0.5`}>{portfolioMeta.performanceSummary.performance != null ? `${portfolioMeta.performanceSummary.performance >= 0 ? '+' : ''}$${formatNumber(Math.abs(portfolioMeta.performanceSummary.performance))}` : ''}</div>
                                            </>
                                        ) : (
                                            <LockBadge />
                                        )}
                                    </div>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>1 Year Return</div>
                                        <div className={`text-sm font-semibold ${signClass(portfolioMeta.performanceSummary.performancePercentage1year)}`}>{(portfolioMeta.performanceSummary.performancePercentage1year ?? 0).toFixed(2)}%</div>
                                        <div className={`text-[11px] ${textSecondary} mt-0.5`}>{portfolioMeta.performanceSummary.performance1year != null ? `${portfolioMeta.performanceSummary.performance1year >= 0 ? '+' : ''}$${formatNumber(Math.abs(portfolioMeta.performanceSummary.performance1year))}` : ''}</div>
                                    </div>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>3 Year Return</div>
                                        {hasValidAccess ? (
                                            <>
                                                <div className={`text-sm font-semibold ${signClass(portfolioMeta.performanceSummary.performancePercentage3year)}`}>{(portfolioMeta.performanceSummary.performancePercentage3year ?? 0).toFixed(2)}%</div>
                                                <div className={`text-[11px] ${textSecondary} mt-0.5`}>{portfolioMeta.performanceSummary.performance3year != null ? `${portfolioMeta.performanceSummary.performance3year >= 0 ? '+' : ''}$${formatNumber(Math.abs(portfolioMeta.performanceSummary.performance3year))}` : ''}</div>
                                            </>
                                        ) : (
                                            <LockBadge />
                                        )}
                                    </div>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>5 Year Return</div>
                                        {hasValidAccess ? (
                                            <>
                                                <div className={`text-sm font-semibold ${signClass(portfolioMeta.performanceSummary.performancePercentage5year)}`}>{(portfolioMeta.performanceSummary.performancePercentage5year ?? 0).toFixed(2)}%</div>
                                                <div className={`text-[11px] ${textSecondary} mt-0.5`}>{portfolioMeta.performanceSummary.performance5year != null ? `${portfolioMeta.performanceSummary.performance5year >= 0 ? '+' : ''}$${formatNumber(Math.abs(portfolioMeta.performanceSummary.performance5year))}` : ''}</div>
                                            </>
                                        ) : (
                                            <LockBadge />
                                        )}
                                    </div>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>Since Inception</div>
                                        {hasValidAccess ? (
                                            <>
                                                <div className={`text-sm font-semibold ${signClass(portfolioMeta.performanceSummary.performanceSinceInceptionPercentage)}`}>{(portfolioMeta.performanceSummary.performanceSinceInceptionPercentage ?? 0).toFixed(2)}%</div>
                                                <div className={`text-[11px] ${textSecondary} mt-0.5`}>{portfolioMeta.performanceSummary.performanceSinceInception != null ? `${portfolioMeta.performanceSummary.performanceSinceInception >= 0 ? '+' : ''}$${formatNumber(Math.abs(portfolioMeta.performanceSummary.performanceSinceInception))}` : ''}</div>
                                            </>
                                        ) : (
                                            <LockBadge />
                                        )}
                                    </div>
                                </>
                            ) : (
                                // Fallback static values (existing, reduced sizes)
                                <>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>Recent Quarter</div>
                                        {hasValidAccess ? (
                                            <>
                                                <div className="font-semibold text-sm !text-green-500">12.79%</div>
                                                <div className={`text-[11px] ${textSecondary} mt-0.5`}>+$3.08B</div>
                                            </>
                                        ) : (
                                            <LockBadge />
                                        )}
                                    </div>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>1 Year Return</div>
                                        <div className="font-semibold text-sm !text-green-500">23.56%</div>
                                        <div className={`text-[11px] ${textSecondary} mt-0.5`}>+$6.63B</div>
                                    </div>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>3 Year Return</div>
                                        {hasValidAccess ? (
                                            <>
                                                <div className="font-semibold text-sm !text-green-500">100.77%</div>
                                                <div className={`text-[11px] ${textSecondary} mt-0.5`}>+$11.71B</div>
                                            </>
                                        ) : (
                                            <LockBadge />
                                        )}
                                    </div>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>5 Year Return</div>
                                        {hasValidAccess ? (
                                            <>
                                                <div className="font-semibold text-sm !text-green-500">126.06%</div>
                                                <div className={`text-[11px] ${textSecondary} mt-0.5`}>+$15.44B</div>
                                            </>
                                        ) : (
                                            <LockBadge />
                                        )}
                                    </div>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>Since Inception</div>
                                        {hasValidAccess ? (
                                            <>
                                                <div className="font-semibold text-sm !text-green-500">358.22%</div>
                                                <div className={`text-[11px] ${textSecondary} mt-0.5`}>+$25.91B</div>
                                            </>
                                        ) : (
                                            <LockBadge />
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Main Metrics Grid */}
                    <div className={`rounded-xl border ${cardBorder} ${cardBg} overflow-hidden`}>
                        <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-y md:divide-y-0 divide-gray-700 dark:divide-gray-700">
                            <div className="p-3">
                                <div className={`text-[11px] ${textSecondary} mb-1`}>AUM</div>
                                <div className={`text-sm font-semibold ${textPrimary}`}>${formatNumber(metrics?.aum || 0)}</div>
                            </div>
                            <div className="p-3">
                                <div className={`text-[11px] ${textSecondary} mb-1`}>AUM Growth</div>
                                <div className={`text-sm font-semibold ${signClass(metrics?.aumGrowth ?? 0)}`}>
                                    {metrics && metrics.aumGrowth >= 0 ? '+' : ''}${formatNumber(Math.abs(metrics?.aumGrowth || 0))}
                                </div>
                            </div>
                            <div className="p-3">
                                <div className={`text-[11px] ${textSecondary} mb-1`}>Holdings</div>
                                <div className={`text-sm font-semibold ${textPrimary}`}>{metrics?.holdingCount.toLocaleString()}</div>
                            </div>
                            <div className="p-3">
                                <div className={`text-[11px] ${textSecondary} mb-1`}>Avg Hold Period</div>
                                {hasValidAccess ? (
                                    <>
                                        <div className={`text-sm font-semibold ${textPrimary}`}>
                                            {portfolioMeta?.performanceSummary?.averageHoldingPeriod ?? 19} months
                                        </div>
                                        <div className={`text-[11px] ${textSecondary} mt-0.5`}>
                                            Top 10: {portfolioMeta?.performanceSummary?.averageHoldingPeriodTop10 ?? 45}mo
                                        </div>
                                    </>
                                ) : (
                                    <LockBadge />
                                )}
                            </div>
                            <div className="p-3">
                                <div className={`text-[11px] ${textSecondary} mb-1`}>Turnover Rate</div>
                                {hasValidAccess ? (
                                    <div className={`text-sm font-semibold ${textPrimary}`}>
                                        {portfolioMeta?.performanceSummary?.turnover != null
                                            ? `${(portfolioMeta.performanceSummary.turnover * 100).toFixed(2)}%`
                                            : '19.8%'}
                                    </div>
                                ) : (
                                    <LockBadge />
                                )}
                            </div>
                        </div>
                    </div>


                    {/* S&P 500 Comparison */}
                    <div className={`rounded-xl border ${cardBorder} ${cardBg} overflow-hidden`}>
                        <div className="p-2 border-b border-gray-700">
                            <div className={`text-xs font-medium ${textPrimary}`}>vs S&P 500</div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-gray-700 dark:divide-gray-700">
                            {portfolioMeta?.performanceSummary ? (
                                <>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>Recent</div>
                                        <div className={`font-semibold text-sm ${signClass(portfolioMeta.performanceSummary.performanceRelativeToSP500Percentage)}`}>
                                            {portfolioMeta.performanceSummary.performanceRelativeToSP500Percentage >= 0 ? '+' : ''}{(portfolioMeta.performanceSummary.performanceRelativeToSP500Percentage ?? 0).toFixed(2)}%
                                        </div>
                                    </div>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>1 Year</div>
                                        <div className={`font-semibold text-sm ${signClass(portfolioMeta.performanceSummary.performance1yearRelativeToSP500Percentage)}`}>
                                            {portfolioMeta.performanceSummary.performance1yearRelativeToSP500Percentage >= 0 ? '+' : ''}{(portfolioMeta.performanceSummary.performance1yearRelativeToSP500Percentage ?? 0).toFixed(2)}%
                                        </div>
                                    </div>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>3 Year</div>
                                        <div className={`font-semibold text-sm ${signClass(portfolioMeta.performanceSummary.performance3yearRelativeToSP500Percentage)}`}>
                                            {portfolioMeta.performanceSummary.performance3yearRelativeToSP500Percentage >= 0 ? '+' : ''}{(portfolioMeta.performanceSummary.performance3yearRelativeToSP500Percentage ?? 0).toFixed(2)}%
                                        </div>
                                    </div>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>All Time</div>
                                        <div className={`font-semibold text-sm ${signClass(portfolioMeta.performanceSummary.performanceSinceInceptionRelativeToSP500Percentage)}`}>
                                            {portfolioMeta.performanceSummary.performanceSinceInceptionRelativeToSP500Percentage >= 0 ? '+' : ''}{(portfolioMeta.performanceSummary.performanceSinceInceptionRelativeToSP500Percentage ?? 0).toFixed(2)}%
                                        </div>
                                    </div>
                                </>
                            ) : (
                                // Fallback static values
                                <>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>Recent</div>
                                        <div className={`font-semibold text-sm ${signClass(4.99)}`}>+4.99%</div>
                                    </div>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>1 Year</div>
                                        <div className={`font-semibold text-sm ${signClass(7.5)}`}>+7.50%</div>
                                    </div>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>3 Year</div>
                                        <div className={`font-semibold text-sm ${signClass(14.24)}`}>+14.24%</div>
                                    </div>
                                    <div className="p-3">
                                        <div className={`text-[11px] ${textSecondary} mb-1`}>All Time</div>
                                        <div className={`font-semibold text-sm ${signClass(30.06)}`}>+30.06%</div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Activity Metrics */}
                    <div className={`rounded-xl border ${cardBorder} ${cardBg} overflow-hidden`}>
                        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-gray-700 dark:divide-gray-700">
                            <div className="p-3">
                                <div className={`text-[11px] ${textSecondary} mb-1`}>New</div>
                                {hasValidAccess ? (
                                    <div className={`text-sm font-semibold ${textPrimary}`}>{metrics?.newPositions}</div>
                                ) : (
                                    <LockBadge />
                                )}
                            </div>
                            <div className="p-3">
                                <div className={`text-[11px] ${textSecondary} mb-1`}>Increased</div>
                                {hasValidAccess ? (
                                    <div className={`text-sm font-semibold !text-green-500`}>{metrics?.increased}</div>
                                ) : (
                                    <LockBadge />
                                )}
                            </div>
                            <div className="p-3">
                                <div className={`text-[11px] ${textSecondary} mb-1`}>Reduced</div>
                                {hasValidAccess ? (
                                    <div className={`text-sm font-semibold !text-red-500`}>{metrics?.reduced}</div>
                                ) : (
                                    <LockBadge />
                                )}
                            </div>
                            <div className="p-3">
                                <div className={`text-[11px] ${textSecondary} mb-1`}>Closed</div>
                                {hasValidAccess ? (
                                    <div className={`text-sm font-semibold ${textPrimary}`}>{metrics?.closedPositions}</div>
                                ) : (
                                    <LockBadge />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Holdings Table */}
                <div className={`border ${cardBorder} ${cardBg} rounded-xl overflow-hidden`}>
                    <div className="p-3 border-b border-gray-700">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
                            <h3 className={`font-bold text-sm ${textPrimary}`}>
                                Portfolio Holdings
                                {isFilterLoading && <span className="ml-2 text-xs text-blue-500">Loading...</span>}
                            </h3>
                            {portfolioMeta && (
                                <div className={`text-[11px] ${textSecondary}`}>
                                    {filteredHoldings.length > 0 && `Showing ${filteredHoldings.length} of `}
                                    {portfolioMeta.total.toLocaleString()} holdings
                                    {(searchQuery || assetTypeFilter !== 'all') && ' (filtered)'}
                                </div>
                            )}
                        </div>

                        {/* Filters */}
                        <div className="flex flex-col items-center sm:flex-row gap-3">
                            {/* Search Filter */}
                            <div className="flex-1 flex gap-2">
                                <input
                                    type="text"
                                    placeholder="Search by name or symbol..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch();
                                        }
                                    }}
                                    className={`flex-1 px-3 py-2 text-xs rounded-lg border ${cardBorder} ${cardBg} ${textPrimary} placeholder:${textSecondary} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                />
                                <button
                                    onClick={handleSearch}
                                    className={`px-4 py-2 text-xs rounded-lg transition-colors btn active hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                >
                                    Search
                                </button>
                            </div>

                            {/* Asset Type Filter */}
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    onClick={() => setAssetTypeFilter('stock')}
                                    className={`px-3 py-2 text-xs rounded-lg transition-colors ${
                                        assetTypeFilter === 'stock'
                                            ? 'active-bg'
                                            : `${cardBg} ${textSecondary} border ${cardBorder} hover:${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`
                                    }`}
                                >
                                    Stocks
                                </button>
                                <button
                                    onClick={() => setAssetTypeFilter('call')}
                                    className={`px-3 py-2 text-xs rounded-lg transition-colors ${
                                        assetTypeFilter === 'call'
                                            ? 'active-bg'
                                            : `${cardBg} ${textSecondary} border ${cardBorder} hover:${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`
                                    }`}
                                >
                                    Calls
                                </button>
                                <button
                                    onClick={() => setAssetTypeFilter('put')}
                                    className={`px-3 py-2 text-xs rounded-lg transition-colors ${
                                        assetTypeFilter === 'put'
                                            ? 'active-bg'
                                            : `${cardBg} ${textSecondary} border ${cardBorder} hover:${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`
                                    }`}
                                >
                                    Puts
                                </button>
                                {(searchInput || assetTypeFilter !== 'stock') && (
                                    <button
                                        onClick={() => {
                                            setSearchInput('');
                                            setSearchQuery('');
                                            setAssetTypeFilter('stock');
                                        }}
                                        className={`px-3 py-2 text-xs rounded-lg transition-colors border ${cardBorder} ${textSecondary} hover:${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}
                                    >
                                        Clear Filters
                                    </button>
                                )}
                            </div>

                            {/* Restrictions Button */}
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    onClick={() => setShowRestrictions(!showRestrictions)}
                                    className={`px-3 py-2 text-xs rounded-lg transition-colors border ${cardBorder} ${textSecondary} hover:${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}
                                >
                                    {showRestrictions ? 'Hide' : 'Show'} Restrictions
                                </button>
                            </div>

                            {/* Restrictions Panel */}
                            {showRestrictions && (
                                <div className={`mt-3 p-3 rounded-lg border ${cardBorder} ${cardBg}`}>
                                    <h3 className={`text-sm font-semibold mb-3 ${textPrimary}`}>Restrictions</h3>
                                    <div className="space-y-2">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={columnVisibility.marketValue}
                                                onChange={(e) => setColumnVisibility(prev => ({ ...prev, marketValue: e.target.checked }))}
                                                className="w-3 h-3 text-blue-500 rounded focus:ring-blue-500"
                                            />
                                            <span className={`text-xs ${textPrimary}`}>Market Value</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={columnVisibility.portfolioWeight}
                                                onChange={(e) => setColumnVisibility(prev => ({ ...prev, portfolioWeight: e.target.checked }))}
                                                className="w-3 h-3 text-blue-500 rounded focus:ring-blue-500"
                                            />
                                            <span className={`text-xs ${textPrimary}`}>Portfolio Weight</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={columnVisibility.shares}
                                                onChange={(e) => setColumnVisibility(prev => ({ ...prev, shares: e.target.checked }))}
                                                className="w-3 h-3 text-blue-500 rounded focus:ring-blue-500"
                                            />
                                            <span className={`text-xs ${textPrimary}`}>Shares</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={columnVisibility.changeInStake}
                                                onChange={(e) => setColumnVisibility(prev => ({ ...prev, changeInStake: e.target.checked }))}
                                                className="w-3 h-3 text-blue-500 rounded focus:ring-blue-500"
                                            />
                                            <span className={`text-xs ${textPrimary}`}>Change in Stake</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={columnVisibility.capitalFlow}
                                                onChange={(e) => setColumnVisibility(prev => ({ ...prev, capitalFlow: e.target.checked }))}
                                                className="w-3 h-3 text-blue-500 rounded focus:ring-blue-500"
                                            />
                                            <span className={`text-xs ${textPrimary}`}>Capital Flow</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="overflow-x-auto relative">
                        {isFilterLoading && (
                            <div className="absolute inset-0 bg-gray-900/20 dark:bg-gray-900/40 backdrop-blur-[1px] z-10 flex items-center justify-center">
                                <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
                                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                    <span className={`text-sm ${textPrimary}`}>Loading...</span>
                                </div>
                            </div>
                        )}
                        <table className="min-w-full text-xs">
                            <thead className={`${theme === 'dark' ? 'bg-gray-700/40' : 'bg-gray-50'} text-[11px]`}>
                                <tr>
                                    <th className="text-left px-3 py-2">#</th>
                                    <th className="text-left px-3 py-2">Name</th>
                                    {columnVisibility.marketValue && <th className="text-right px-3 py-2">Market Value</th>}
                                    {columnVisibility.portfolioWeight && <th className="text-right px-3 py-2">Portfolio Weight</th>}
                                    {columnVisibility.shares && <th className="text-right px-3 py-2">Shares</th>}
                                    {columnVisibility.changeInStake && <th className="text-right px-3 py-2">Change in Stake</th>}
                                    {columnVisibility.capitalFlow && <th className="text-right px-3 py-2">Capital Flow</th>}
                                </tr>
                            </thead>
                            <tbody className={isFilterLoading ? 'opacity-30' : ''}>
                                {filteredHoldings.length > 0 ? (
                                    filteredHoldings.map((holding, idx) => (
                                        <tr
                                            key={idx}
                                            onClick={hasValidAccess ? () => handleStockClick(holding.symbol) : undefined}
                                            className={`border-t ${cardBorder} ${hasValidAccess ? 'cursor-pointer ' + (theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50') : 'cursor-default'}`}
                                        >
                                            <td className="px-3 py-2 font-semibold">{idx + 1}</td>
                                            <td className="px-3 py-2">
                                                {hasValidAccess ? (
                                                    <div>
                                                        <div className={`font-semibold ${textPrimary}`}>{holding.symbol || 'N/A'}</div>
                                                        <div className={`text-[11px] ${textSecondary}`}>
                                                            {holding.nameOfIssuer}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <LockBadge />
                                                )}
                                            </td>
                                            {columnVisibility.marketValue && <td className="px-3 py-2 text-right">${formatNumber(holding.value)}</td>}
                                            {columnVisibility.portfolioWeight && <td className="px-3 py-2 text-right">{holding.portfolioWeight.toFixed(2)}%</td>}
                                            {columnVisibility.shares && <td className="px-3 py-2 text-right">{formatNumber(holding.shares, 0)}</td>}
                                            {columnVisibility.changeInStake && (
                                                hasValidAccess ? (
                                                    <td className={`${holding.changeInStake >= 0 ? 'text-green-500' : 'text-red-500'} px-3 py-2 text-right`}>
                                                        {holding.changeInStake >= 0 ? '+' : ''}{(holding.changeInStake * 100).toFixed(2)}%
                                                    </td>
                                                ) : (
                                                    <td className="px-3 py-2 text-right">
                                                        <div className="flex justify-end">
                                                            <LockBadge />
                                                        </div>
                                                    </td>
                                                )
                                            )}
                                            {columnVisibility.capitalFlow && (
                                                hasValidAccess ? (
                                                    <td className={`font-semibold ${holding.changeInValue >= 0 ? 'text-green-500' : 'text-red-500'} px-3 py-2 text-right`}>
                                                        {holding.changeInValue >= 0 ? '+' : ''}${formatNumber(Math.abs(holding.changeInValue))}
                                                    </td>
                                                ) : (
                                                    <td className="px-3 py-2 text-right">
                                                        <div className="flex justify-end">
                                                            <LockBadge />
                                                        </div>
                                                    </td>
                                                )
                                            )}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={2 + Object.values(columnVisibility).filter(Boolean).length} className="px-3 py-8 text-center">
                                            <span className={textSecondary}>No holdings match your filters</span>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Infinite scroll sentinel */}
                    <div ref={sentinelRef} className="p-2" />
                    {loadingMore && (
                        <div className="p-4 text-center">
                            <span className={textSecondary}>Loading more holdings...</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InstitutionPortfolio;
